import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, UserCheck, Briefcase, Building2,
  ArrowRight, MapPin, Phone, Mail, LayoutDashboard,
  CheckCircle, Sparkles, Award, Lock,
  Globe, Zap, Target, Search, Handshake,
  ChevronRight, Crown, TrendingUp, Users, Star
} from 'lucide-react';
import '../styles/LoginPage.css';

/* ─── Tilt Card — shake fix: rAF throttled, no CSS transform conflict ─── */
const TiltCard = ({ children, className }) => {
  const cardRef   = useRef(null);
  const rafRef    = useRef(null);
  const isHovered = useRef(false);

  const handleMouseEnter = useCallback(() => {
    isHovered.current = true;
    const card = cardRef.current;
    if (card) {
      /* Fast response on enter, slow spring on leave */
      card.style.transition =
        'transform 0.12s linear, box-shadow 0.6s cubic-bezier(0.16,1,0.3,1), border-color 0.6s cubic-bezier(0.16,1,0.3,1)';
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isHovered.current) return;
    if (rafRef.current) return; /* throttle */

    const clientX = e.clientX;
    const clientY = e.clientY;

    rafRef.current = requestAnimationFrame(() => {
      const card = cardRef.current;
      if (!card) { rafRef.current = null; return; }

      const rect    = card.getBoundingClientRect();
      const x       = clientX - rect.left;
      const y       = clientY - rect.top;
      const centerX = rect.width  / 2;
      const centerY = rect.height / 2;

      /* Clamp rotation — keeps it subtle, reduces perceived shake */
      const rotateX = Math.max(-5, Math.min(5, ((y - centerY) / centerY) * -5));
      const rotateY = Math.max(-5, Math.min(5, ((x - centerX) / centerX) *  5));

      card.style.transform =
        `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(8px)`;

      rafRef.current = null;
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    isHovered.current = false;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    const card = cardRef.current;
    if (card) {
      card.style.transition =
        'transform 0.9s cubic-bezier(0.16,1,0.3,1), box-shadow 0.6s cubic-bezier(0.16,1,0.3,1), border-color 0.6s cubic-bezier(0.16,1,0.3,1)';
      card.style.transform = 'perspective(1200px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
    }
  }, []);

  return (
    <div
      ref={cardRef}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
};

/* ─── Portal Card ─── */
const PortalCard = ({ id, title, description, features, trustItems, icon: Icon, type, reverse }) => {
  const navigate = useNavigate();

  const handleLogin = (userType) => {
    if (userType === 'Admin')     navigate('/admin-login');
    else if (userType === 'Candidate') navigate('/candidate-login');
    else if (userType === 'Employer')  navigate('/customerLogin');
    else if (userType === 'Recruiter') navigate('/recruiter-login');
    else if (userType === 'College')   navigate('/college-login');
  };

  return (
    <section
      id={`${id}-section`}
      className={`to-portal-section to-reveal${reverse ? ' to-reverse' : ''}`}
    >
      <div className="to-section-content">

        {/* TEXT COLUMN */}
        <div className="to-text-col to-reveal-left">
          <div className="to-pill-badge">
            <Icon size={14} /> <span>{title} Portal</span>
          </div>
          <h2 className="to-section-heading">{title} <em>Access</em></h2>
          <p className="to-description">{description}</p>
          <div className="to-feature-grid">
            {features.map((f, i) => (
              <div
                key={i}
                className="to-feature-item"
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <CheckCircle size={16} className="to-gold-icon" />
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CARD COLUMN */}
        <div className="to-card-col to-reveal-right">
          <TiltCard className={`to-login-card to-card-${type}`}>
            <div className="to-card-glow" />
            <div className="to-card-shine" />

            <div className="to-card-header">
              <div className="to-icon-box">
                <Icon size={32} strokeWidth={1.5} />
              </div>
              <div>
                <h3>{title}</h3>
                <span className="to-subtitle">Secure Entry</span>
              </div>
            </div>

            <div className="to-divider" />

            {/* Trust badges — makes card richer */}
            {trustItems && (
              <div className="to-card-trust">
                {trustItems.map((t, i) => (
                  <div key={i} className="to-trust-badge">
                    <t.icon size={12} />
                    <span>{t.label}</span>
                  </div>
                ))}
              </div>
            )}

            <button className="to-main-btn" onClick={() => handleLogin(title)}>
              <span>Proceed to Login</span>
              <ArrowRight size={18} className="to-btn-arrow" />
            </button>

            <p className="to-card-footer-text">
              <Lock size={11} /> Protected enterprise gateway
            </p>
          </TiltCard>
        </div>

      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════ */
const LoginPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('');
  const [scrollY, setScrollY]             = useState(0);
  const [mousePos, setMousePos]           = useState({ x: 0, y: 0 });
  const rafRef = useRef(null);

  /* ── Scroll tracking (rAF throttled) ── */
  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        rafRef.current = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  /* ── Mouse tracking for hero ambient ── */
  useEffect(() => {
    const onMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth  - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  /* ── Intersection Observer for reveal animations ── */
  useEffect(() => {
    const elements = document.querySelectorAll('.to-reveal');
    if (!('IntersectionObserver' in window)) {
      elements.forEach(el => el.classList.add('to-in-view'));
      return;
    }

    elements.forEach(el => el.classList.add('to-anim-ready'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('to-in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    elements.forEach(el => observer.observe(el));

    /* Safety net — show everything after 2 s if JS is slow */
    const safety = setTimeout(() => {
      document.querySelectorAll('.to-reveal:not(.to-in-view)')
        .forEach(el => el.classList.add('to-in-view'));
    }, 2000);

    return () => {
      elements.forEach(el => observer.unobserve(el));
      clearTimeout(safety);
    };
  }, []);

  const handleNavClick = (userType) => {
    setActiveSection(userType.toLowerCase());
    const el = document.getElementById(`${userType.toLowerCase()}-section`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  /* ── Portal data ── */
  const portals = [
    {
      id: 'admin', title: 'Admin', type: 'admin', icon: ShieldCheck, reverse: false,
      description: 'Orchestrate the entire ecosystem with enterprise-grade controls, real-time analytics, and uncompromising system governance.',
      features: ['System Analytics', 'User Management', 'Content Control', 'Security Audits'],
      trustItems: [{ icon: Lock, label: 'SSL Secured' }, { icon: ShieldCheck, label: 'Role Gated' }],
    },
    {
      id: 'recruiter', title: 'Recruiter', type: 'recruiter', icon: Briefcase, reverse: true,
      description: 'Identify and secure top-tier talent using advanced screening, intelligent pipelines, and refined acquisition technologies.',
      features: ['Smart Sourcing', 'Interview Tools', 'Pipeline Logic', 'Hiring Metrics'],
      trustItems: [{ icon: TrendingUp, label: 'Live Pipeline' }, { icon: Users, label: 'Team Access' }],
    },
    {
      id: 'employer', title: 'Employer', type: 'customer', icon: LayoutDashboard, reverse: false,
      description: 'Experience bespoke hospitality services. Manage your talent portfolio and access exclusive premium benefits with concierge precision.',
      features: ['Portfolio View', 'Concierge Support', 'Service History', 'Premium Access'],
      trustItems: [{ icon: Star, label: 'Premium Tier' }, { icon: Lock, label: 'Encrypted' }],
    },
    {
      id: 'college', title: 'College', type: 'college', icon: Building2, reverse: true,
      description: 'Bridge the gap between academic excellence and industry standards with sophisticated placement suites and corporate connectivity.',
      features: ['Campus Drives', 'Student Tracking', 'Alumni Network', 'Corporate Connect'],
      trustItems: [{ icon: Award, label: 'Accredited' }, { icon: Users, label: 'Batch Mgmt' }],
    },
    {
      id: 'candidate', title: 'Candidate', type: 'candidate', icon: UserCheck, reverse: false,
      description: 'Build a distinguished professional profile and unlock opportunities with the world\'s most prestigious luxury hospitality brands.',
      features: ['Executive Profile', 'AI Job Matching', 'Career Tracking', 'Direct Applications'],
      trustItems: [{ icon: Sparkles, label: 'AI Powered' }, { icon: Globe, label: 'Global Reach' }],
    },
  ];

  return (
    <div className="talent-oza-landing">

      {/* ══════════════════════════════════
          HERO
      ══════════════════════════════════ */}
      <section className="to-hero">
        <div
          className="to-hero-bg"
          style={{
            transform: `translate3d(${mousePos.x * -15}px, ${scrollY * 0.35 + mousePos.y * -10}px, 0) scale(${1.05 + scrollY * 0.0002})`,
          }}
        />
        <div className="to-hero-overlay" />
        <div className="to-hero-vignette" />
        <div
          className="to-hero-mesh"
          style={{
            transform: `translate(-50%, -50%) translate3d(${mousePos.x * 20}px, ${mousePos.y * 20}px, 0)`,
          }}
        />

        <div
          className="to-hero-content"
          style={{
            transform: `translateY(${scrollY * 0.25}px)`,
            opacity: Math.max(0, 1 - scrollY / 700),
          }}
        >
          <div className="to-hero-badge to-fade-up">
            <Crown size={12} fill="#C5A059" stroke="none" />
            <span>Premier Hospitality Network</span>
          </div>

          <h1 className="to-fade-up to-delay-1">
            Your Gateway to <br />
            <span className="to-gold-text">Career Excellence</span>
          </h1>

          <p className="to-fade-up to-delay-2">
            A unified hospitality ecosystem connecting elite talent,
            recruiters, and institutions across the globe.
          </p>

          <div className="to-nav-pills to-fade-up to-delay-3">
            {['Admin', 'Recruiter', 'Employer', 'College', 'Candidate'].map(type => (
              <button
                key={type}
                className={`to-pill${activeSection === type.toLowerCase() ? ' active' : ''}`}
                onClick={() => handleNavClick(type)}
              >
                {type}
              </button>
            ))}
          </div>

          <div className="to-scroll-indicator to-fade-up to-delay-4">
            <div className="to-scroll-mouse">
              <div className="to-scroll-wheel" />
            </div>
            <span>Scroll to explore</span>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          MARQUEE
      ══════════════════════════════════ */}
      <section className="to-marquee-section to-reveal">
        <div className="to-marquee-label">
          <Sparkles size={14} /> <span>Serving Across</span>
        </div>
        <div className="to-marquee">
          <div className="to-marquee-track">
            {[
              'Luxury Resorts', 'Five-Star Hotels', 'Fine Dining', 'Boutique Properties',
              'Hospitality Schools', 'Cruise Lines', 'Private Members Clubs', 'Wellness Retreats',
              'Luxury Resorts', 'Five-Star Hotels', 'Fine Dining', 'Boutique Properties',
              'Hospitality Schools', 'Cruise Lines', 'Private Members Clubs', 'Wellness Retreats',
            ].map((item, i) => (
              <span key={i} className="to-marquee-item">
                {item} <span className="to-marquee-dot">◆</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          PILLARS
      ══════════════════════════════════ */}
      <section className="to-pillars to-reveal">
        <div className="to-pillars-inner">
          <div className="to-section-header">
            <span className="to-eyebrow">Why HotelPlacement.ai</span>
            <h2>Built on three uncompromising <em>principles.</em></h2>
          </div>
          <div className="to-pillars-grid">
            {[
              { icon: Sparkles, title: 'Curated Network',     stat: 'Invite-only',   desc: 'An invitation-only ecosystem of vetted luxury hospitality partners and discerning professionals.' },
              { icon: Target,   title: 'Intelligent Matching', stat: '98% accuracy',  desc: 'AI-driven precision pairing talent and opportunities with unmatched accuracy and discretion.' },
              { icon: Award,    title: 'Excellence First',     stat: '5★ standard',  desc: 'Engineered for prestige brands and elite talent who refuse to settle for the ordinary.' },
            ].map((p, i) => (
              <div key={i} className="to-pillar">
                <div className="to-pillar-icon"><p.icon size={28} /></div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                <div className="to-pillar-stat">
                  <Star size={11} fill="currentColor" /> {p.stat}
                </div>
                <div className="to-pillar-line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          SECTION INTRO
      ══════════════════════════════════ */}
      <section className="to-section-intro to-reveal">
        <div className="to-intro-inner">
          <span className="to-eyebrow">Five Portals · One Ecosystem</span>
          <h2>Designed for every role <em>in hospitality.</em></h2>
          <p>
            Each gateway is purpose-built to deliver a seamless, secure,
            and refined experience tailored to your unique needs.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════
          PORTAL SECTIONS
      ══════════════════════════════════ */}
      <div className="to-sections-wrapper">
        {portals.map(p => <PortalCard key={p.id} {...p} />)}
      </div>

      {/* ══════════════════════════════════
          PROCESS TIMELINE
      ══════════════════════════════════ */}
      <section className="to-process to-reveal">
        <div className="to-process-inner">
          <div className="to-section-header">
            <span className="to-eyebrow">The Journey</span>
            <h2>How it <em>works.</em></h2>
          </div>
          <div className="to-process-timeline">
            {[
              { icon: UserCheck,  num: '01', title: 'Register', desc: 'Choose your portal and create a refined profile with verified credentials.' },
              { icon: Search,     num: '02', title: 'Discover',  desc: 'Explore curated opportunities or talent through intelligent recommendations.' },
              { icon: Handshake,  num: '03', title: 'Connect',   desc: 'Engage directly through secure channels with discreet professional communication.' },
              { icon: Zap,        num: '04', title: 'Elevate',   desc: 'Advance your career or grow your team within a world-class hospitality network.' },
            ].map((step, i) => (
              <div
                key={i}
                className="to-process-step"
                style={{ transitionDelay: `${i * 130}ms` }}
              >
                <div className="to-step-number">{step.num}</div>
                <div className="to-step-icon"><step.icon size={22} /></div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          GLOBAL PRESENCE
      ══════════════════════════════════ */}
      <section className="to-global to-reveal">
        <div className="to-global-inner">
          <div className="to-global-text">
            <span className="to-eyebrow">Global Presence</span>
            <h2>From the <em>Gulf</em> to the world.</h2>
            <p>
              Headquartered in Dubai, with a network spanning the Middle East, Europe,
              and Asia-Pacific. Our reach connects exceptional talent with premier
              hospitality destinations across the globe.
            </p>
            <div className="to-region-list">
              {['Middle East', 'Europe', 'Asia-Pacific', 'North America'].map((region, i) => (
                <div
                  key={i}
                  className="to-region"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <Globe size={16} className="to-gold-icon" />
                  <span>{region}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="to-global-visual">
            <div className="to-globe-orb">
              <div className="to-orb-ring to-ring-1" />
              <div className="to-orb-ring to-ring-2" />
              <div className="to-orb-ring to-ring-3" />
              <div className="to-orb-core">
                <Globe size={48} strokeWidth={1} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          CTA BAND
      ══════════════════════════════════ */}
      <section className="to-cta-band to-reveal">
        <div className="to-cta-particles" />
        <div className="to-cta-inner">
          <span className="to-eyebrow to-eyebrow-light">Begin Your Journey</span>
          <h2>Ready to elevate your <em>hospitality journey?</em></h2>
          <p>
            Select your portal above and step into a refined, world-class experience
            designed for the most discerning professionals.
          </p>
          <button
            className="to-cta-button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <span>Choose Your Portal</span>
            <ChevronRight size={18} />
          </button>
        </div>
      </section>

      {/* ══════════════════════════════════
          FOOTER
      ══════════════════════════════════ */}
      <footer className="to-footer">
        <div className="to-footer-content">
          <div className="to-footer-brand">
            <h2>HotelPlacement.ai</h2>
            <p>Redefining Hospitality Recruitment.</p>
            <div className="to-socials">
              <span>LinkedIn</span> · <span>Twitter</span> · <span>Instagram</span>
            </div>
          </div>

          <div className="to-footer-info">
            <div className="to-contact-row"><MapPin size={16} /><span>Aspin Towers, Sheikh Zayed Road, Dubai</span></div>
            <div className="to-contact-row"><Phone size={16} /><span>+971 4250 3121</span></div>
            <div className="to-contact-row"><Mail  size={16} /><span>info@hotelplacement.ae</span></div>
          </div>

          <div className="to-footer-links">
            <button onClick={() => navigate('/about')}>About Us</button>
            <button onClick={() => navigate('/privacy')}>Privacy</button>
            <button onClick={() => navigate('/terms')}>Terms</button>
          </div>
        </div>

        <div className="to-copyright">
          © 2026 TalentOza. All rights reserved.
        </div>
      </footer>

    </div>
  );
};

export default LoginPage;